import { studioToolKeySchema, studioTool } from '@ads/shared';
import type { ReactNode } from 'react';
import * as Ic from '../components/icons';
import { dateTime } from '../lib/format';
import { useSession, useUsage } from '../lib/queries';

/* Configurações · uso: o que esta organização gastou no mês, em tokens do
   ADS e em tokens do modelo — e, com chave de admin, o gasto real da conta
   da Anthropic. A Anthropic não informa o saldo de créditos por API. */

const n = (x: number) => x.toLocaleString('pt-BR');
const usd = (x: number) => x.toLocaleString('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const toolName = (k: string) => { const r = studioToolKeySchema.safeParse(k); return r.success ? studioTool(r.data).name : k; };

export function UsageConfig({ tabs }: { tabs: ReactNode }) {
  const { me } = useSession();
  const q = useUsage();
  const u = q.data;
  const month = u ? new Date(u.period.from).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
  const pct = u ? Math.round((u.credits.available / Math.max(1, u.credits.quota)) * 100) : 0;

  return (
    <section className="view on" data-v="config">
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Configurações</span><b>{me.org.name}</b></div>
          <h1>Uso</h1>
          <p className="lede">Quanto a {me.org.name} já gerou neste mês e quanto ainda pode gerar — e, se você ligar, o gasto real da conta da Anthropic.</p>
        </div>
        <div className="tools">{tabs}</div>
      </div>

      {q.isPending && <p className="sub">Carregando…</p>}
      {u && (
        <div className="grid2 config-grid">
          <div className="stack">
            <div className="card">
              <h3><Ic.Bars />Tokens do ADS</h3>
              <div className="use-big">
                <b>{n(u.credits.available)}</b><span>de {n(u.credits.quota)} disponíveis</span>
              </div>
              <span className="align-t use-track"><i style={{ width: `${pct}%` }} /></span>
              <p className="sub">Cada peça do Studio usa um valor fixo (Manifesto 45, Landing 95, Website 210…). Se a geração falhar, os tokens voltam. Ler os materiais e interpretar o objetivo não consome tokens do ADS — só do modelo.</p>
            </div>

            <div className="card">
              <h3><Ic.Sparkle />Em {month}</h3>
              <div className="feed-facts use-facts">
                <div><span>Peças geradas</span><b>{n(u.month.generations)}</b></div>
                <div><span>Tokens do ADS usados</span><b>{n(u.month.credits)}</b></div>
                <div><span>Leituras de materiais e objetivo</span><b>{n(u.month.readings.count)}</b></div>
                {u.month.edits.count > 0 && <div><span>Alterações pela conversa</span><b>{n(u.month.edits.count)} · {n(u.month.edits.credits)} tokens</b></div>}
                <div><span>Tokens do modelo</span><b className="plain">{n(u.month.modelTokens)}</b></div>
                {u.month.failed > 0 && <div><span>Falharam (devolvidos)</span><b>{n(u.month.failed)}</b></div>}
              </div>
              {u.month.byTool.length > 0 ? (
                <ul className="site-list use-list">
                  {u.month.byTool.map(t => (
                    <li key={t.tool}>
                      <div className="sl-main"><b>{toolName(t.tool)}</b><span>{t.count} {t.count === 1 ? 'peça' : 'peças'} · {n(t.tokens)} tokens do modelo</span></div>
                      <span className="use-cr">{n(t.credits)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="sub">Nenhuma peça gerada neste mês ainda.</p>}
            </div>
          </div>

          <div className="stack">
            <div className="card">
              <h3><Ic.Cube />Modelo</h3>
              <p className="sub">
                {u.provider.name === 'anthropic'
                  ? <>Os agentes escrevem com o Claude — <span className="mono">{u.provider.model}</span>.</>
                  : <>Modo demonstração: roteiros a partir do DNA, sem custo. Para usar o Claude, defina <span className="mono">ANTHROPIC_API_KEY</span> e <span className="mono">ADS_MODEL</span> no EasyPanel.</>}
              </p>
            </div>

            <div className="card">
              <h3><Ic.Target />Conta da Anthropic</h3>
              {u.anthropic.configured ? (
                u.anthropic.error ? (
                  <p className="cfg-status bad"><Ic.Warning />Não foi possível ler o gasto: {u.anthropic.error}</p>
                ) : (
                  <div className="stack" style={{ gap: 14 }}>
                    <div className="use-big">
                      <b>{usd(u.anthropic.costUsd ?? 0)}</b><span>gastos em {month}</span>
                    </div>
                    {u.anthropic.remainingUsd !== null && u.anthropic.budgetUsd !== null && (
                      <>
                        <span className="align-t use-track"><i style={{ width: `${Math.round((u.anthropic.remainingUsd / Math.max(0.01, u.anthropic.budgetUsd)) * 100)}%` }} /></span>
                        <p className="sub"><b>{usd(u.anthropic.remainingUsd)}</b> restantes de um orçamento de {usd(u.anthropic.budgetUsd)} (ANTHROPIC_BUDGET_USD).</p>
                      </>
                    )}
                    <p className="sub">É o gasto da conta inteira da Anthropic — inclui outras aplicações que usem a mesma organização. A Anthropic atualiza em até alguns minutos{u.anthropic.updatedAt ? `; lido em ${dateTime(u.anthropic.updatedAt)}` : ''}.</p>
                  </div>
                )
              ) : (
                <div className="stack" style={{ gap: 12 }}>
                  <p className="sub">A Anthropic não informa o saldo de créditos pela API. O que dá para mostrar aqui é o <b>gasto do mês</b>, lido do relatório de custos da sua organização.</p>
                  <p className="sub">Para ligar, crie uma <b>chave de admin</b> em platform.claude.com → Settings → Admin keys e defina no EasyPanel:</p>
                  <pre className="use-env">ANTHROPIC_ADMIN_KEY=sk-ant-admin…{'\n'}ANTHROPIC_BUDGET_USD=50   # opcional: mostra quanto resta</pre>
                  <p className="sub">Se a conta ficar sem créditos, a geração para com a mensagem “A conta da Anthropic está sem créditos” e os tokens do ADS voltam.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
